/**
 * Hub alert contract and pure rules (no browser APIs), shared by the alert UI and its tests.
 *
 * Any backend can alert Hub users of a facility with Platform template `hub_alert`. The push data
 * carries: `hubAlertType`, `alertKey`, `facilityId`, `facilityName`, `entityType`, `entityId`,
 * `ring` (LOUD | QUIET), `priority` (URGENT | NORMAL), `details` (JSON label/value list), plus
 * the rendered title, body and `deeplinkRelativePath` (same-origin Hub path with
 * `?hubAlert={type}&facilityId={id}&entityId={id}`). A new alert type works without UI changes:
 * it opens its own link. Register a type below only when its link depends on the user's roles.
 */

export const ALL_FACILITIES = 'ALL_FACILITIES';

export type HubAlertRing = 'LOUD' | 'QUIET';
export type HubAlertPriority = 'URGENT' | 'NORMAL';
export type HubAlertDetail = { label: string; value: string };
type RoleList = Array<{ name?: string } | string> | undefined;

export type HubPushData = Record<string, string | undefined>;

export type HubAlert = {
  /** Null for a push that is not a Hub alert (shown as a plain notice). */
  type: string | null;
  /** Identifies one alert occurrence: duplicates replace instead of ringing twice. */
  key: string;
  facilityId: number | null;
  facilityName: string | null;
  entityType: string | null;
  entityId: string | null;
  title: string;
  body: string;
  /** Same-origin path to open for this alert. */
  url: string;
  ring: HubAlertRing;
  priority: HubAlertPriority;
  details: Array<HubAlertDetail>;
};

export type HubAlertLink = { type: string; facilityId: number | null; entityId: string | null };

type HubAlertTypeDefinition = {
  /** Default when the push carries no `ring`. */
  ring: HubAlertRing;
  /** Role-aware destination; without it the alert opens its own link. */
  destination?: (link: HubAlertLink, roles: RoleList) => string;
};

/** Roles that can open package pages in Hub (packages resource masterRoles, plus SUPER_ADMIN). */
const PACKAGE_PAGE_ROLES = ['SUPER_ADMIN', 'WAREHOUSE_ADMIN', 'WAREHOUSE_MANAGER', 'WAREHOUSE_EXECUTIVE'];

const roleNames = (roles: RoleList): Array<string> =>
  (roles ?? []).map(role => (typeof role === 'string' ? role : role?.name ?? '')).filter(Boolean);

export const canOpenPackagePages = (roles: RoleList) => {
  const names = roleNames(roles);
  return PACKAGE_PAGE_ROLES.some(role => names.includes(role));
};

export const HUB_ALERT_TYPES: Record<string, HubAlertTypeDefinition> = {
  NEW_WIP_ORDER: {
    ring: 'LOUD',
    // Pickers cannot open package pages; they work from picklists.
    destination: ({ entityId }, roles) =>
      !canOpenPackagePages(roles) ? '/picklists' : entityId ? `/packages/show/${entityId}` : '/packages',
  },
  // SCM: a gatepass order to pick and send (e.g. Try at Home). Gatepass order pages need
  // executive or above; pickers pick through picklists. STN_INBOUND opens its own link.
  STN_PICKUP: {
    ring: 'LOUD',
    destination: ({ entityId }, roles) =>
      !canOpenPackagePages(roles)
        ? '/picklists'
        : entityId
          ? `/gatepass-orders/show/${entityId}`
          : '/outgoing-stock-transfers',
  },
};

/**
 * Users whose devices register for Hub alerts (union of every alert type's recipients). Extend it
 * when a new alert type targets other Hub roles.
 */
export const HUB_ALERT_ROLES = [
  'WAREHOUSE_ADMIN',
  'WAREHOUSE_MANAGER',
  'WAREHOUSE_EXECUTIVE',
  'PICKER',
] as const;

export const isHubAlertRecipient = (roles: RoleList) => {
  const names = roleNames(roles);
  return HUB_ALERT_ROLES.some(role => names.includes(role));
};

const toId = (value: string | null | undefined): number | null => {
  if (!value || !/^\d+$/.test(value.trim())) {
    return null;
  }
  return Number(value.trim());
};

/** An unrendered template reference: `${x}` or the quiet `$!{x}` the hub_alert template uses. */
const UNRENDERED = /^\$!?\{/;

/** Trimmed value, or null when blank or an unrendered template reference. */
const text = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  return trimmed && !UNRENDERED.test(trimmed) ? trimmed : null;
};

/** Entity ids are numeric; anything else (for example `../..`) must not reach a route. */
const entityIdOf = (value: string | null | undefined) => {
  const id = text(value);
  return id && /^\d+$/.test(id) ? id : null;
};

/** Reads `?hubAlert={type}&facilityId=..&entityId=..` (and the first release's `hubAlert=wip&packageId=..`). */
export const parseHubAlertLink = (search: string): HubAlertLink | null => {
  const params = new URLSearchParams(search);
  const raw = text(params.get('hubAlert'));
  if (!raw) {
    return null;
  }
  const legacyWip = raw === 'wip';
  return {
    type: legacyWip ? 'NEW_WIP_ORDER' : raw,
    facilityId: toId(params.get('facilityId')),
    entityId: entityIdOf(params.get('entityId')) ?? entityIdOf(params.get('packageId')),
  };
};

const ALERT_LINK_PARAMS = ['hubAlert', 'facilityId', 'entityId', 'packageId'];

/** The alert's own path without the alert and tracking parameters. */
const cleanPath = (url: string, origin: string) => {
  const parsed = new URL(url, origin);
  if (parsed.origin !== origin || parsed.pathname.startsWith('//')) {
    return '/';
  }
  [...parsed.searchParams.keys()]
    .filter(key => ALERT_LINK_PARAMS.includes(key) || key.startsWith('utm_'))
    .forEach(key => parsed.searchParams.delete(key));
  const search = parsed.searchParams.toString();
  return parsed.pathname + (search ? `?${search}` : '');
};

const parseDetails = (raw: string | undefined): Array<HubAlertDetail> => {
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed
          .filter(
            (item): item is HubAlertDetail =>
              typeof item?.label === 'string' && typeof item?.value === 'string',
          )
          .slice(0, 8)
      : [];
  } catch {
    return [];
  }
};

export const parseHubAlert = (data: HubPushData | undefined, origin: string): HubAlert => {
  const title = text(data?.title) ?? 'Hub';
  const body = text(data?.body) ?? '';
  let url = '/';
  let link: HubAlertLink | null = null;
  try {
    const parsed = new URL(data?.deeplinkRelativePath || '/', origin);
    // `//host/...` is same-origin here but leaves Hub when navigated to.
    if (parsed.origin === origin && !parsed.pathname.startsWith('//')) {
      url = parsed.pathname + parsed.search;
      link = parseHubAlertLink(parsed.search);
    }
  } catch {
    url = '/';
  }
  const type = text(data?.hubAlertType) ?? link?.type ?? (data?.type === 'HUB_NEW_WIP_ORDER' ? 'NEW_WIP_ORDER' : null);
  const definition = type ? HUB_ALERT_TYPES[type] : undefined;
  const entityId = entityIdOf(data?.entityId) ?? link?.entityId ?? null;
  const facilityId = toId(data?.facilityId) ?? link?.facilityId ?? null;
  const ring = data?.ring === 'LOUD' || data?.ring === 'QUIET' ? data.ring : definition?.ring ?? 'QUIET';
  return {
    type,
    key:
      text(data?.alertKey) ??
      (type && entityId ? `${type}-${entityId}-${facilityId ?? ''}` : `${title}|${body}`),
    facilityId,
    facilityName: text(data?.facilityName),
    entityType: text(data?.entityType) ?? (type === 'NEW_WIP_ORDER' ? 'PACKAGE' : null),
    entityId,
    title,
    body,
    url,
    ring: type ? ring : 'QUIET',
    priority: data?.priority === 'URGENT' ? 'URGENT' : 'NORMAL',
    details: parseDetails(data?.details),
  };
};

/** Where an alert (or a tapped alert link) should land for this user. */
export const alertDestination = (
  link: HubAlertLink,
  url: string,
  roles: RoleList,
  origin: string,
) => {
  const destination = HUB_ALERT_TYPES[link.type]?.destination;
  return destination ? destination(link, roles) : cleanPath(url, origin);
};

/** A path inside Hub to navigate to; anything that could leave Hub becomes `/`. */
export const hubPath = (url: string | null | undefined, origin: string) => {
  try {
    const parsed = new URL(url || '/', origin);
    return parsed.origin === origin && !parsed.pathname.startsWith('//')
      ? parsed.pathname + parsed.search
      : '/';
  } catch {
    return '/';
  }
};

export const linkOf = (alert: HubAlert): HubAlertLink | null =>
  alert.type ? { type: alert.type, facilityId: alert.facilityId, entityId: alert.entityId } : null;

/**
 * Whether an alert belongs to the facility on screen (or All Facilities). One for another
 * facility names it and offers Switch and open (how it rings: alertPresentation).
 */
export const matchesSelection = (
  alertFacilityId: number | null,
  selected: number | typeof ALL_FACILITIES | undefined,
) => {
  if (selected === ALL_FACILITIES || alertFacilityId === null) {
    return true;
  }
  return selected === alertFacilityId;
};

/**
 * Facilities that share one building (a warehouse and its returns or QC-failed books, a store and
 * its back office): staff who selected one of them are also at the others. Production ids.
 */
export const SAME_SITE_FACILITIES: ReadonlyArray<ReadonlyArray<number>> = [
  [480, 481], // Samalkha RTV, Samalkha Warehouse
  [1, 252, 407], // Dwarka Warehouse, RTV, QC Failed
  [3, 8, 47, 51], // Ambience VK Store, Popup, Dark Store, Back Office
  [52, 53, 309], // MOI Store, Popup, Dark Store
  [82, 83], // Ireo Store, Back Office
  [102, 103], // Select Citywalk Store, Back Office
  [154, 155], // Pacific Store, Back Office
];

/** The two facilities are one place (the same one, or in one building). */
export const sameSite = (a: number, b: number) =>
  a === b || SAME_SITE_FACILITIES.some(site => site.includes(a) && site.includes(b));

/**
 * How this screen presents an alert:
 * - `LOUD`: rings until acknowledged (the alarm bar).
 * - `SUBTLE`: one soft chime, a calm bar for a few seconds and a count on the bell. A loud alert
 *   becomes subtle when it is for another of the operator's facilities than the one selected, or
 *   when All Facilities is selected; urgent alerts (Express, Try at Home) always ring.
 * - `QUIET`: added to the bell only (quiet types).
 * Unknown selection (Hub still loading), an alert without a facility, an operator with one
 * facility, or "Ring for all my facilities" on this device keep the loud ring.
 */
export type HubAlertPresentation = 'LOUD' | 'SUBTLE' | 'QUIET';

export const alertPresentation = (
  alert: Pick<HubAlert, 'ring' | 'priority' | 'facilityId'>,
  context: {
    selected: number | typeof ALL_FACILITIES | null | undefined;
    userFacilityIds: ReadonlyArray<number> | undefined;
    ringAll: boolean;
  },
): HubAlertPresentation => {
  if (alert.ring !== 'LOUD') return 'QUIET';
  const { selected, userFacilityIds, ringAll } = context;
  if (
    alert.priority === 'URGENT' ||
    ringAll ||
    selected === null ||
    selected === undefined ||
    alert.facilityId === null ||
    (userFacilityIds?.length ?? 0) <= 1
  ) {
    return 'LOUD';
  }
  if (selected === ALL_FACILITIES) return 'SUBTLE';
  return sameSite(selected, alert.facilityId) ? 'LOUD' : 'SUBTLE';
};

/** Show the facility on alert cards whenever it is not obvious from the screen. */
export const shouldShowFacility = (
  selected: number | typeof ALL_FACILITIES | undefined,
  userFacilityCount: number,
) => selected === ALL_FACILITIES || userFacilityCount > 1;

export type HubAlertSupport = 'supported' | 'ios-install-required' | 'unsupported';

export type HubAlertEnvironment = {
  userAgent: string;
  maxTouchPoints: number;
  hasNotification: boolean;
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  isStandalone: boolean;
};

export const isAppleMobile = (env: Pick<HubAlertEnvironment, 'userAgent' | 'maxTouchPoints'>) =>
  /iPad|iPhone|iPod/.test(env.userAgent) ||
  (/Macintosh/.test(env.userAgent) && env.maxTouchPoints > 1);

/** iPhone/iPad web push works only for a Home Screen app (iOS 16.4+). */
export const detectHubAlertSupport = (env: HubAlertEnvironment): HubAlertSupport => {
  const capable = env.hasNotification && env.hasServiceWorker && env.hasPushManager;
  if (isAppleMobile(env) && !env.isStandalone) {
    return 'ios-install-required';
  }
  return capable ? 'supported' : 'unsupported';
};

/** Keeps the same alert from ringing twice (two backend paths, or two pushes) within a window. */
export class RecentAlertFilter {
  private readonly seen = new Map<string, number>();

  constructor(private readonly windowMs = 10 * 60 * 1000) {}

  isNew(key: string, now = Date.now()) {
    for (const [seenKey, at] of this.seen) {
      if (now - at > this.windowMs) {
        this.seen.delete(seenKey);
      }
    }
    if (this.seen.has(key)) {
      return false;
    }
    this.seen.set(key, now);
    return true;
  }
}
